/**
 * Events Pending Review Card Component
 * 
 * PROTOTYPE vs PRODUCTION:
 * - Currently uses simple table with mock data for rapid prototyping
 * - Production-ready DataTable implementation is commented out and ready to activate
 * - When API endpoint is available, search for "TODO: PRODUCTION" comments for transition steps
 * 
 * Transition Steps:
 * 1. Uncomment DataTable in template (lines 29-52 in HTML)
 * 2. Comment out simple table section (lines 54-158 in HTML) 
 * 3. Remove loadPendingReviewData() calls and window references in component
 * 4. Update API endpoint URL to real endpoint
 */

import { CommonModule } from '@angular/common';
import { Component, input, signal, inject, ViewChild, ElementRef } from '@angular/core';
import { MetricCardComponent } from 'src/app/core/components/metric-card';
import { EventsPendingReviewResponse, PendingReviewItem, PendingReviewItemsResponse } from 'src/app/core/models/analytics/es-models';
import { EsAnalyticsService } from '../../services/es-analytics.service';
import { NotificationService } from '@progress/kendo-angular-notification';
import { DatatableComponent } from 'src/app/core/components/datatable/datatable.component';
import { DialogModule } from '@progress/kendo-angular-dialog';

@Component({
  selector: 'app-es-events-pending-review-card',
  standalone: true,
  imports: [CommonModule, MetricCardComponent, DatatableComponent, DialogModule],
  templateUrl: './es-events-pending-review-card.component.html',
  styleUrl: './es-events-pending-review-card.component.scss',
})
export class EsEventsPendingReviewCardComponent {
  eventsPendingReview = input<EventsPendingReviewResponse>();
  
  private esAnalyticsService = inject(EsAnalyticsService);
  private notificationService = inject(NotificationService);
  
  showModal = signal(false);
  pendingReviewData = signal<PendingReviewItemsResponse | null>(null);
  
  // TODO: PRODUCTION - DataTable configuration for when API is ready
  dataUrl = '/api/v1/email-security/analytics/pending-review-items/';
  columns = [
    { 
      data: 'eventName', 
      name: 'event_name', 
      title: 'Event Name', 
      type: 'string',
      render: (data: any, _type: any, _row: any) => {
        return data?.length > 50 ? data.substring(0, 50) + '...' : data;
      }
    },
    { 
      data: 'severity', 
      name: 'severity', 
      title: 'Severity', 
      type: 'select',
      render: (data: any, _type: any, _row: any) => {
        const colorClass = data === 'Critical' ? 'danger' : 
                          data === 'High' ? 'warning' : 
                          data === 'Medium' ? 'info' : 'secondary';
        return `<span class="badge badge-${colorClass}">${data}</span>`;
      }
    },
    { 
      data: 'dateTime', 
      name: 'date_time', 
      title: 'Date & Time', 
      type: 'datetime',
      render: (data: any, _type: any, _row: any) => {
        return new Date(data).toLocaleString();
      }
    },
    { data: 'threatType', name: 'threat_type', title: 'Threat Type', type: 'string' },
    { data: 'user', name: 'user', title: 'User', type: 'string' },
    { data: 'mailbox', name: 'mailbox', title: 'Mailbox', type: 'string' },
    { 
      data: 'details', 
      name: 'details', 
      title: 'Details', 
      type: 'string',
      render: (data: any, type: any, row: any) => {
        return data?.length > 30 ? `<span title="${data}">${data.substring(0, 30)}...</span>` : data;
      }
    },
    { 
      data: null, 
      name: 'actions', 
      title: 'Actions', 
      type: 'actions', 
      orderable: false,
      render: (data: any, type: any, row: any) => {
        const actions = row.available_actions || [];
        let buttonsHtml = '<div class="btn-group" role="group">';
        
        actions.forEach((action: string) => {
          const buttonClass = this.getDataTableButtonClass(action);
          const escapedRow = JSON.stringify(row).replace(/"/g, '&quot;');
          const escapedAction = action.replace(/"/g, '&quot;');
          
          buttonsHtml += `
            <button type="button" class="${buttonClass} me-1" 
              onclick="window.pendingReviewComponent.onActionClick(${escapedRow}, '${escapedAction}')" 
              title="${action}">
              ${action}
            </button>
          `;
        });
        
        buttonsHtml += '</div>';
        return buttonsHtml;
      }
    }
  ];

  onCardClick() {
    this.showModal.set(true);
    // TODO: PRODUCTION - Remove loadPendingReviewData() call when using DataTable (DataTable handles data loading)
    this.loadPendingReviewData();
    // TODO: PRODUCTION - Remove window reference when using DataTable (DataTable handles button callbacks differently)
    (window as any).pendingReviewComponent = this;
  }

  closeModal() {
    this.showModal.set(false);
    // TODO: PRODUCTION - Remove window reference cleanup when using DataTable
    delete (window as any).pendingReviewComponent;
  }

  // TODO: PRODUCTION - Remove this method when using DataTable (DataTable handles server-side data loading)
  private loadPendingReviewData() {
    this.esAnalyticsService.getPendingReviewItems(null).subscribe(data => {
      this.pendingReviewData.set(data);
    });
  }

  onAlertUser(item: PendingReviewItem) {
    this.notificationService.show({
      content: `User ${item.user} has been alerted about this phishing attempt`,
      animation: { type: 'slide', duration: 300 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'warning', icon: true },
      closable: false,
      hideAfter: 3000
    });
  }

  onDismiss(item: PendingReviewItem) {
    this.notificationService.show({
      content: `Event "${item.eventName}" has been dismissed as false positive`,
      animation: { type: 'slide', duration: 300 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'info', icon: true },
      closable: false,
      hideAfter: 3000
    });
  }

  onMoveToSpam(item: PendingReviewItem) {
    this.notificationService.show({
      content: `Email has been moved to spam folder for ${item.user}`,
      animation: { type: 'slide', duration: 300 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'warning', icon: true },
      closable: false,
      hideAfter: 3000
    });
  }

  onQuarantine(item: PendingReviewItem) {
    this.notificationService.show({
      content: `Email has been quarantined for further review`,
      animation: { type: 'slide', duration: 300 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'error', icon: true },
      closable: false,
      hideAfter: 3000
    });
  }

  onAddNotes(item: PendingReviewItem) {
    this.notificationService.show({
      content: `Notes have been added to event for ${item.user}`,
      animation: { type: 'slide', duration: 300 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'success', icon: true },
      closable: false,
      hideAfter: 3000
    });
  }

  onAnomalyException(item: PendingReviewItem) {
    this.notificationService.show({
      content: `Anomaly exception has been created for ${item.user}`,
      animation: { type: 'slide', duration: 300 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'info', icon: true },
      closable: false,
      hideAfter: 3000
    });
  }

  onResetPassword(item: PendingReviewItem) {
    this.notificationService.show({
      content: `Password reset initiated for ${item.user}`,
      animation: { type: 'slide', duration: 300 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'warning', icon: true },
      closable: false,
      hideAfter: 3000
    });
  }

  onBlockUser(item: PendingReviewItem) {
    this.notificationService.show({
      content: `User ${item.user} has been temporarily blocked`,
      animation: { type: 'slide', duration: 300 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'error', icon: true },
      closable: false,
      hideAfter: 3000
    });
  }

  onApproveApp(item: PendingReviewItem) {
    this.notificationService.show({
      content: `Application has been approved for ${item.user}`,
      animation: { type: 'slide', duration: 300 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'success', icon: true },
      closable: false,
      hideAfter: 3000
    });
  }

  // Generic action handler that routes to specific methods
  onActionClick(item: PendingReviewItem, actionType: string) {
    switch (actionType) {
      case 'Alert User of Phishing':
        this.onAlertUser(item);
        break;
      case 'Dismiss':
        this.onDismiss(item);
        break;
      case 'Move to Spam':
        this.onMoveToSpam(item);
        break;
      case 'Quarantine':
        this.onQuarantine(item);
        break;
      case 'Add Notes':
        this.onAddNotes(item);
        break;
      case 'Anomaly Exception':
        this.onAnomalyException(item);
        break;
      case 'Reset Password':
        this.onResetPassword(item);
        break;
      case 'Block User':
        this.onBlockUser(item);
        break;
      case 'Approve This App':
        this.onApproveApp(item);
        break;
      default:
        console.warn(`Unknown action type: ${actionType}`);
    }
  }

  // Helper method to get button CSS class based on action type
  getActionButtonClass(actionType: string): string {
    switch (actionType) {
      case 'Alert User of Phishing':
        return 'action-btn action-btn-alert';
      case 'Dismiss':
        return 'action-btn action-btn-dismiss';
      case 'Move to Spam':
        return 'action-btn action-btn-spam';
      case 'Quarantine':
        return 'action-btn action-btn-quarantine';
      case 'Add Notes':
        return 'action-btn action-btn-notes';
      case 'Anomaly Exception':
        return 'action-btn action-btn-exception';
      case 'Reset Password':
        return 'action-btn action-btn-reset';
      case 'Block User':
        return 'action-btn action-btn-block';
      case 'Approve This App':
        return 'action-btn action-btn-approve';
      default:
        return 'action-btn action-btn-default';
    }
  }

  // Helper method to get DataTable button CSS class based on action type
  getDataTableButtonClass(actionType: string): string {
    switch (actionType) {
      case 'Alert User of Phishing':
        return 'btn btn-sm btn-danger';
      case 'Dismiss':
        return 'btn btn-sm btn-secondary';
      case 'Move to Spam':
        return 'btn btn-sm btn-warning';
      case 'Quarantine':
        return 'btn btn-sm btn-outline-danger';
      case 'Add Notes':
        return 'btn btn-sm btn-primary';
      case 'Anomaly Exception':
        return 'btn btn-sm btn-info';
      case 'Reset Password':
        return 'btn btn-sm btn-warning';
      case 'Block User':
        return 'btn btn-sm btn-danger';
      case 'Approve This App':
        return 'btn btn-sm btn-success';
      default:
        return 'btn btn-sm btn-outline-secondary';
    }
  }
}
